import { useState, useCallback, useEffect, useRef } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/storage";

const CACHE_KEY_PREFIX = "video_thumbnail_";
const MAX_THUMB_WIDTH = 320;
const JPEG_QUALITY = 0.6;
const DEFAULT_CONCURRENCY = 3;

let didPurgeLegacyCache = false;

/**
 * Purge legacy localStorage data-URL thumbnails (fills browser quota).
 * Safe to call repeatedly; each call removes matching keys.
 */
export function purgeLegacyLocalStorageThumbnails() {
  if (typeof localStorage === "undefined") {
    return 0;
  }
  let removed = 0;
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_KEY_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => {
      localStorage.removeItem(key);
      removed += 1;
    });
  } catch (error) {
    console.warn("Error purging legacy thumbnail cache:", error);
  }
  return removed;
}

function purgeLegacyCacheOnce() {
  if (didPurgeLegacyCache) return;
  didPurgeLegacyCache = true;
  purgeLegacyLocalStorageThumbnails();
}

function scaleCanvasDimensions(width, height, maxWidth = MAX_THUMB_WIDTH) {
  if (!width || !height) {
    return { width: maxWidth, height: Math.round(maxWidth * 0.75) };
  }
  if (width <= maxWidth) {
    return { width, height };
  }
  const scale = maxWidth / width;
  return {
    width: maxWidth,
    height: Math.round(height * scale),
  };
}

function describeVideoLoadError(event) {
  const code = event?.target?.error?.code;
  // MEDIA_ERR_SRC_NOT_SUPPORTED is often a 403/404, not CORS
  if (code === 4) {
    return "Video load failed";
  }
  if (code === 2) {
    return "Network error";
  }
  return "Video load error";
}

async function refreshMessageGroupVideoUrl(organizationId, video) {
  const groupId = video?.metadata?.groupId;
  const messageId = video?.metadata?.messageId;
  if (!organizationId || !groupId || !messageId) {
    return null;
  }
  try {
    const path = `${organizationId}/messageGroups/${groupId}/${messageId}.mp4`;
    return await firebase.storage().ref(path).getDownloadURL();
  } catch (error) {
    return null;
  }
}

function isChatMessageVideo(video) {
  return Boolean(video?.metadata?.groupId && video?.metadata?.messageId);
}

function isMessageGroupsStorageUrl(url) {
  return typeof url === "string" && url.includes("/messageGroups/");
}

/**
 * Shared hook for generating and caching video thumbnails with lazy loading support
 * @param {Array} videos - Array of video objects with url and id properties
 * @param {Object} options - Configuration options
 * @param {boolean} options.lazy - Whether to use lazy loading (default: false)
 * @param {number} options.maxRetries - Maximum retry attempts (default: 2)
 * @param {number} options.retryDelay - Base retry delay in ms (default: 1000)
 * @param {number} options.loadTimeout - Timeout for video loading in ms (default: 10000)
 * @param {number} options.concurrency - Max concurrent thumbnail generations (default: 3)
 * @param {string|null} options.organizationId - Org id for refreshing messageGroups Storage URLs
 * @returns {Object} - Object containing thumbnail URLs, loading states, and utility functions
 */
const useVideoThumbnails = (videos = [], options = {}) => {
  const {
    lazy = false,
    maxRetries = 2,
    retryDelay = 1000,
    loadTimeout = 10000,
    concurrency = DEFAULT_CONCURRENCY,
    organizationId = null,
  } = options;

  const [thumbnails, setThumbnails] = useState({});
  const [loadingVideoIds, setLoadingVideoIds] = useState(new Set());
  const [failedVideoIds, setFailedVideoIds] = useState(new Set());
  const [intersectionObserver, setIntersectionObserver] = useState(null);

  const videoElementRefs = useRef({});
  const observedElements = useRef(new Set());

  // Refs keep generation logic stable and avoid effect/callback dependency loops
  const thumbnailsRef = useRef({});
  const loadingIdsRef = useRef(new Set());
  const failedIdsRef = useRef(new Set());
  const retryCountRef = useRef({});
  const refreshedUrlIdsRef = useRef(new Set());
  const urlOverridesRef = useRef({});
  const activeCountRef = useRef(0);
  const queueRef = useRef([]);
  const processQueueRef = useRef(() => {});
  const mountedRef = useRef(true);

  const videosRef = useRef(videos);
  videosRef.current = videos;

  const optionsRef = useRef({
    maxRetries,
    retryDelay,
    loadTimeout,
    concurrency,
    organizationId,
  });
  optionsRef.current = {
    maxRetries,
    retryDelay,
    loadTimeout,
    concurrency,
    organizationId,
  };

  useEffect(() => {
    mountedRef.current = true;
    purgeLegacyCacheOnce();
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const setLoadingId = useCallback((videoId, isLoading) => {
    if (isLoading) {
      loadingIdsRef.current.add(videoId);
    } else {
      loadingIdsRef.current.delete(videoId);
    }
    if (!mountedRef.current) return;
    setLoadingVideoIds(new Set(loadingIdsRef.current));
  }, []);

  const markFailed = useCallback((videoId, error) => {
    // Missing/deleted chat videos are common; keep console quieter than error-level
    console.warn(`Video ${videoId} failed thumbnail generation:`, error);
    failedIdsRef.current.add(videoId);
    loadingIdsRef.current.delete(videoId);
    if (!mountedRef.current) return;
    setFailedVideoIds(new Set(failedIdsRef.current));
    setLoadingVideoIds(new Set(loadingIdsRef.current));
  }, []);

  const markSuccess = useCallback((videoId, thumbnailUrl) => {
    thumbnailsRef.current[videoId] = thumbnailUrl;
    loadingIdsRef.current.delete(videoId);
    if (!mountedRef.current) return;
    setThumbnails((prev) => ({
      ...prev,
      [videoId]: thumbnailUrl,
    }));
    setLoadingVideoIds(new Set(loadingIdsRef.current));
  }, []);

  const runThumbnailCapture = useCallback(
    (video, sourceUrl) =>
      new Promise((resolve) => {
        const { loadTimeout: timeoutMs } = optionsRef.current;
        const videoEl = document.createElement("video");
        videoEl.crossOrigin = "anonymous";
        videoEl.muted = true;
        videoEl.playsInline = true;
        videoEl.preload = "metadata";
        videoEl.src = sourceUrl;

        let settled = false;
        const finish = (result) => {
          if (settled) return;
          settled = true;
          clearTimeout(timeoutId);
          videoEl.removeAttribute("src");
          videoEl.load();
          videoEl.remove();
          resolve(result);
        };

        const timeoutId = setTimeout(() => {
          finish({ ok: false, error: "Loading timeout" });
        }, timeoutMs);

        videoEl.onloadedmetadata = () => {
          try {
            const duration = videoEl.duration;
            if (!Number.isFinite(duration) || duration <= 0) {
              videoEl.currentTime = 0;
            } else {
              const seekTo = Math.min(
                Math.max(duration * 0.2, Math.min(1, duration / 2)),
                Math.max(duration - 0.5, 0),
              );
              videoEl.currentTime = seekTo;
            }
          } catch (err) {
            finish({ ok: false, error: err });
          }
        };

        videoEl.onseeked = () => {
          try {
            const { width, height } = scaleCanvasDimensions(
              videoEl.videoWidth,
              videoEl.videoHeight,
            );
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(videoEl, 0, 0, width, height);
            const thumbnailUrl = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
            finish({ ok: true, thumbnailUrl });
          } catch (error) {
            finish({ ok: false, error });
          }
        };

        videoEl.onerror = (event) => {
          finish({ ok: false, error: describeVideoLoadError(event) });
        };
      }),
    [],
  );

  const processOneVideo = useCallback(
    async (video) => {
      const { maxRetries, retryDelay, organizationId: orgId } =
        optionsRef.current;

      if (
        !video?.id ||
        !video?.url ||
        thumbnailsRef.current[video.id] ||
        failedIdsRef.current.has(video.id)
      ) {
        return;
      }

      const attempts = retryCountRef.current[video.id] || 0;
      if (attempts >= maxRetries) {
        failedIdsRef.current.add(video.id);
        if (mountedRef.current) {
          setFailedVideoIds(new Set(failedIdsRef.current));
        }
        return;
      }

      setLoadingId(video.id, true);

      if (attempts > 0) {
        await new Promise((r) =>
          setTimeout(r, retryDelay * Math.pow(2, attempts - 1)),
        );
      }

      let sourceUrl = urlOverridesRef.current[video.id] || video.url;

      // Chat uploads: resolve a fresh authenticated URL first so we don't
      // hit a stale-token 403 and then a second getDownloadURL 404.
      if (
        orgId &&
        isChatMessageVideo(video) &&
        !refreshedUrlIdsRef.current.has(video.id)
      ) {
        refreshedUrlIdsRef.current.add(video.id);
        const freshUrl = await refreshMessageGroupVideoUrl(orgId, video);
        if (!freshUrl) {
          retryCountRef.current[video.id] = maxRetries;
          markFailed(video.id, "Video file missing");
          return;
        }
        urlOverridesRef.current[video.id] = freshUrl;
        sourceUrl = freshUrl;
      }

      const result = await runThumbnailCapture(video, sourceUrl);

      if (!mountedRef.current) {
        loadingIdsRef.current.delete(video.id);
        return;
      }

      if (result.ok) {
        markSuccess(video.id, result.thumbnailUrl);
        return;
      }

      // Don't retry missing/forbidden media — re-queue only burns Storage requests
      retryCountRef.current[video.id] = maxRetries;
      markFailed(video.id, result.error);
    },
    [markFailed, markSuccess, runThumbnailCapture, setLoadingId],
  );

  const processQueue = useCallback(() => {
    const { concurrency: maxConcurrent } = optionsRef.current;
    while (
      activeCountRef.current < maxConcurrent &&
      queueRef.current.length > 0
    ) {
      const next = queueRef.current.shift();
      if (
        !next?.id ||
        thumbnailsRef.current[next.id] ||
        failedIdsRef.current.has(next.id) ||
        loadingIdsRef.current.has(next.id)
      ) {
        continue;
      }
      activeCountRef.current += 1;
      processOneVideo(next)
        .catch((error) => {
          markFailed(next.id, error);
        })
        .finally(() => {
          activeCountRef.current = Math.max(0, activeCountRef.current - 1);
          processQueueRef.current();
        });
    }
  }, [markFailed, processOneVideo]);

  processQueueRef.current = processQueue;

  const enqueueVideos = useCallback((videoList) => {
    if (!Array.isArray(videoList) || videoList.length === 0) return;
    videoList.forEach((video) => {
      if (
        !video?.id ||
        !video?.url ||
        thumbnailsRef.current[video.id] ||
        failedIdsRef.current.has(video.id) ||
        loadingIdsRef.current.has(video.id)
      ) {
        return;
      }
      const alreadyQueued = queueRef.current.some((v) => v.id === video.id);
      if (!alreadyQueued) {
        queueRef.current.push(video);
      }
    });
    processQueueRef.current();
  }, []);

  const generateThumbnailForVideo = useCallback(
    (video) => {
      enqueueVideos([video]);
    },
    [enqueueVideos],
  );

  const generateThumbnails = useCallback(() => {
    enqueueVideos(videosRef.current);
  }, [enqueueVideos]);

  // Setup intersection observer for lazy loading
  useEffect(() => {
    if (!lazy) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const videoId = entry.target.getAttribute("data-video-id");
            const video = videosRef.current.find((v) => v.id === videoId);
            if (video) {
              enqueueVideos([video]);
              observer.unobserve(entry.target);
              observedElements.current.delete(entry.target);
            }
          }
        });
      },
      {
        rootMargin: "50px",
        threshold: 0.1,
      },
    );

    setIntersectionObserver(observer);

    return () => {
      observer.disconnect();
      observedElements.current.clear();
    };
  }, [lazy, enqueueVideos]);

  const registerElement = useCallback(
    (element, videoId) => {
      if (!lazy || !intersectionObserver || !element) return;

      if (!observedElements.current.has(element)) {
        element.setAttribute("data-video-id", videoId);
        intersectionObserver.observe(element);
        observedElements.current.add(element);
        videoElementRefs.current[videoId] = element;
      }
    },
    [lazy, intersectionObserver],
  );

  // Auto-generate thumbnails for non-lazy mode
  useEffect(() => {
    if (lazy || !videos || videos.length === 0) return undefined;

    const timeoutId = setTimeout(() => {
      enqueueVideos(videos);
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [lazy, videos, enqueueVideos]);

  const clearCacheForVideos = useCallback((videoIds) => {
    videoIds.forEach((videoId) => {
      delete thumbnailsRef.current[videoId];
      delete urlOverridesRef.current[videoId];
      refreshedUrlIdsRef.current.delete(videoId);
      retryCountRef.current[videoId] = 0;
      failedIdsRef.current.delete(videoId);
    });
    if (!mountedRef.current) return;
    setThumbnails((prev) => {
      const next = { ...prev };
      videoIds.forEach((id) => {
        delete next[id];
      });
      return next;
    });
    setFailedVideoIds(new Set(failedIdsRef.current));
  }, []);

  const resetFailedVideos = useCallback(() => {
    failedIdsRef.current.clear();
    retryCountRef.current = {};
    refreshedUrlIdsRef.current.clear();
    if (!mountedRef.current) return;
    setFailedVideoIds(new Set());
  }, []);

  const resetAll = useCallback(() => {
    thumbnailsRef.current = {};
    loadingIdsRef.current.clear();
    failedIdsRef.current.clear();
    retryCountRef.current = {};
    refreshedUrlIdsRef.current.clear();
    urlOverridesRef.current = {};
    queueRef.current = [];
    activeCountRef.current = 0;
    if (!mountedRef.current) return;
    setThumbnails({});
    setLoadingVideoIds(new Set());
    setFailedVideoIds(new Set());
  }, []);

  return {
    thumbnails,
    loadingVideoIds,
    loadingThumbnails: loadingVideoIds.size > 0,
    failedVideoIds,
    generateThumbnailForVideo,
    generateThumbnails,
    registerElement,
    resetFailedVideos,
    resetAll,
    clearCacheForVideos,
    isLoading: (videoId) => loadingIdsRef.current.has(videoId),
    hasFailed: (videoId) => failedIdsRef.current.has(videoId),
    hasThumbnail: (videoId) => !!thumbnails[videoId],
  };
};

export default useVideoThumbnails;
